import { LAYOUT_DEFAULT, type AvailableLayoutOption } from "@features/presentationLayout/types/presentationLayout";

export type ReportEntityScope = "church" | "district" | "nation";

export type ReportLensKey = "church" | "office" | "children" | "district_origin";

/** Public template tab on a district or nation department report. */
export const REPORT_TABS = ["church", "district", "nation"] as const;

export type ReportTab = (typeof REPORT_TABS)[number];

export const REPORT_TAB_TITLES: Record<ReportTab, string> = {
  church: "Church Templates",
  district: "District Templates",
  nation: "Nation Templates",
};

const ORIGIN_BY_TAB: Record<ReportTab, "CHURCH" | "DISTRICT" | "NATION"> = {
  church: "CHURCH",
  district: "DISTRICT",
  nation: "NATION",
};

type QueryKeys = {
  layout: string;
  view: string;
  bucket: string;
  from: string;
  to: string;
  page: string;
};

const CHURCH_REPORT_KEYS: QueryKeys = {
  layout: "layout",
  view: "view",
  bucket: "bucket",
  from: "from",
  to: "to",
  page: "page",
};

const TAB_KEYS: Record<ReportTab, QueryKeys> = {
  church: {
    layout: "churchLayout",
    view: "churchView",
    bucket: "churchBucket",
    from: "churchFrom",
    to: "churchTo",
    page: "churchPage",
  },
  district: {
    layout: "districtLayout",
    view: "districtView",
    bucket: "districtBucket",
    from: "districtFrom",
    to: "districtTo",
    page: "districtPage",
  },
  nation: {
    layout: "nationLayout",
    view: "nationView",
    bucket: "nationBucket",
    from: "nationFrom",
    to: "nationTo",
    page: "nationPage",
  },
};

export function reportTabsForScope(scope: ReportEntityScope): ReportTab[] {
  if (scope === "church") return ["church"];
  if (scope === "district") return ["church", "district"];
  return ["church", "district", "nation"];
}

export function isReportTab(value: string): value is ReportTab {
  return (REPORT_TABS as readonly string[]).includes(value);
}

export function originForTab(tab: ReportTab): "CHURCH" | "DISTRICT" | "NATION" {
  return ORIGIN_BY_TAB[tab];
}

/** `section_view_meta` key for this tab. Church reports use `church`; a nation District tab uses `district_origin`. */
export function lensKeyForTab(scope: ReportEntityScope, tab: ReportTab): ReportLensKey {
  if (scope === "church") return "church";
  if (tab === "church") return "children";
  if (tab === "district" && scope === "nation") return "district_origin";
  return "office";
}

export function tabForLensKey(scope: ReportEntityScope, section: ReportLensKey): ReportTab {
  if (scope === "church" || section === "church" || section === "children") return "church";
  if (section === "district_origin") return "district";
  return scope === "nation" ? "nation" : "district";
}

export function queryKeysForTab(scope: ReportEntityScope, tab: ReportTab): QueryKeys {
  if (scope === "church") return CHURCH_REPORT_KEYS;
  return TAB_KEYS[tab];
}

export function queryKeysForLens(scope: ReportEntityScope, section: ReportLensKey): QueryKeys {
  return queryKeysForTab(scope, tabForLensKey(scope, section));
}

/** Shared Default plus the named layouts that belong on this tab. */
export function layoutsForTab(layouts: AvailableLayoutOption[], origin: string): AvailableLayoutOption[] {
  const sharedDefault = layouts.find((option) => option.layout === LAYOUT_DEFAULT) ?? {
    layout: LAYOUT_DEFAULT,
    presentation_layout_id: null,
    name: "Default",
    origin_scope: null,
  };
  return [
    sharedDefault,
    ...layouts.filter((option) => option.layout !== LAYOUT_DEFAULT && option.origin_scope === origin),
  ];
}
